import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Search, Filter, ChevronDown, X, Package } from 'lucide-react';
import ContactForm from './ContactForm';
import ProductDetailModal from './ProductDetailModal';
import PartnerProductCard from './PartnerProductCard';
import ENV from '../config/env';

// Animation presets
const fadeIn = {
    initial: { opacity: 0, y: 50 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.8 }
};

// Helper: Check if string is a color hex
const isColorHex = (str) => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(str);
};

// Component: Product card for main products
const ProductCard = ({ product, onClick }) => {
    const { images, name, price, shortDesc, category, variants } = product;

    return (
        <motion.div
            {...fadeIn}
            className="border rounded-lg overflow-hidden transition-all cursor-pointer hover:border-2 hover:border-primary hover:shadow-lg hover:shadow-primary/20 bg-white h-full flex flex-col"
            onClick={() => onClick(product)}
        >
            <div className="h-48 relative bg-gray-100 flex-shrink-0">
                <img
                    src={ENV.FILE_SERVER_URL + (images?.[0] || `${ENV.IMAGE_BASE_PATH}default-product.jpg`)}
                    alt={name}
                    className="w-full h-full object-contain p-4"
                    onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `${ENV.FILE_SERVER_URL}/images/default-product.jpg`;
                    }}
                />
            </div>
            <div className="p-4 flex flex-col flex-1">
                <div className="flex justify-between items-start gap-2 mb-2">
                    <h3 className="text-base font-semibold text-gray-900 break-words line-clamp-2 flex-1">
                        {name}
                    </h3>
                    <span className="text-primary font-medium text-sm whitespace-nowrap flex-shrink-0">
                        {price}
                    </span>
                </div>
                <p className="text-gray-600 text-xs mb-3 break-words line-clamp-3">
                    {shortDesc}
                </p>

                <div className="flex justify-between items-center mt-auto pt-2">
                    <span className="bg-primary/10 text-primary text-xs px-3 py-2 rounded-full truncate max-w-[70%]">
                        {category}
                    </span>
                    <div className="flex items-center text-primary text-xs font-bold flex-shrink-0">
                        <span>Ver detalles</span>
                        <ChevronRight size={12} className="ml-1" />
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

// Component: Partner products filter bar (unchanged)
const PartnerFilterBar = ({
    mainCategories,
    subCategories,
    activeCategory,
    activeSubCategory,
    onCategoryChange,
    onSubCategoryChange,
    totalProducts,
    onClearFilters,
    searchTerm,
    onSearchChange
}) => {
    const [showFilters, setShowFilters] = useState(false);
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [showSubCategoryDropdown, setShowSubCategoryDropdown] = useState(false);

    const currentSubCategories = subCategories && activeCategory ? subCategories[activeCategory] || ['Todos'] : ['Todos'];

    return (
        <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
            {/* Search and filter toggle */}
            <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                        type="text"
                        placeholder="Buscar productos..."
                        value={searchTerm}
                        onChange={(e) => onSearchChange(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all shadow-sm"
                    />
                </div>

                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="md:hidden flex items-center justify-center gap-2 px-4 py-3 bg-primary/10 text-primary rounded-xl hover:bg-primary/20 transition-colors"
                >
                    <Filter size={18} />
                    <span>Filtros</span>
                    <ChevronDown size={16} className={`transform transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </button>

                <div className="hidden md:flex items-center px-4 bg-primary/5 rounded-xl text-primary font-medium">
                    {totalProducts} productos
                </div>
            </div>

            {/* Filters section */}
            <div className={`mt-4 ${showFilters ? 'block' : 'hidden md:block'}`}>
                <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
                    {/* Category filter */}
                    <div className="relative w-full md:w-64">
                        <label className="block text-xs font-medium text-gray-500 mb-1 ml-1">
                            Categoría
                        </label>
                        <button
                            onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                            onBlur={() => setTimeout(() => setShowCategoryDropdown(false), 200)}
                            className="w-full px-4 py-2.5 text-left bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all shadow-sm flex items-center justify-between group hover:border-primary/50"
                        >
                            <span className="text-gray-700 font-medium">{activeCategory}</span>
                            <ChevronDown size={18} className={`text-gray-400 transition-transform duration-200 ${showCategoryDropdown ? 'rotate-180' : ''} group-hover:text-primary`} />
                        </button>

                        {showCategoryDropdown && (
                            <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto py-1">
                                {mainCategories && mainCategories.map((cat) => (
                                    <button
                                        key={cat}
                                        className={`w-full px-4 py-2.5 text-left hover:bg-primary/5 transition-colors ${activeCategory === cat ? 'bg-primary/10 text-primary font-medium border-l-4 border-primary' : 'text-gray-700'
                                            }`}
                                        onClick={() => {
                                            onCategoryChange(cat);
                                            setShowCategoryDropdown(false);
                                        }}
                                    >
                                        {cat}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Subcategory filter */}
                    {currentSubCategories && currentSubCategories.length > 1 && (
                        <div className="relative w-full md:w-64">
                            <label className="block text-xs font-medium text-gray-500 mb-1 ml-1">
                                Subcategoría
                            </label>
                            <button
                                onClick={() => setShowSubCategoryDropdown(!showSubCategoryDropdown)}
                                onBlur={() => setTimeout(() => setShowSubCategoryDropdown(false), 200)}
                                className="w-full px-4 py-2.5 text-left bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all shadow-sm flex items-center justify-between group hover:border-primary/50"
                            >
                                <span className="text-gray-700 font-medium">{activeSubCategory}</span>
                                <ChevronDown size={18} className={`text-gray-400 transition-transform duration-200 ${showSubCategoryDropdown ? 'rotate-180' : ''} group-hover:text-primary`} />
                            </button>

                            {showSubCategoryDropdown && (
                                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto py-1">
                                    {currentSubCategories.map((sub) => (
                                        <button
                                            key={sub}
                                            className={`w-full px-4 py-2.5 text-left hover:bg-primary/5 transition-colors ${activeSubCategory === sub ? 'bg-primary/10 text-primary font-medium border-l-4 border-primary' : 'text-gray-700'
                                                }`}
                                            onClick={() => {
                                                onSubCategoryChange(sub);
                                                setShowSubCategoryDropdown(false);
                                            }}
                                        >
                                            {sub}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Clear filters button */}
                    {(activeCategory !== 'Todos' || activeSubCategory !== 'Todos' || searchTerm) && (
                        <button
                            onClick={onClearFilters}
                            className="mt-4 md:mt-7 px-4 py-2.5 text-sm bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-colors flex items-center gap-2 font-medium"
                        >
                            <X size={16} />
                            <span>Limpiar filtros</span>
                        </button>
                    )}

                    <div className="md:hidden w-full text-center text-sm text-gray-500 mt-2">
                        {totalProducts} productos encontrados
                    </div>
                </div>
            </div>
        </div>
    );
};

// Main Products Component
const Products = () => {
    // Main products state
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedProductType, setSelectedProductType] = useState('main'); // 'main' or 'partner'
    const [activeCategory, setActiveCategory] = useState('Todos');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showContactForm, setShowContactForm] = useState(false);
    const [productToQuote, setProductToQuote] = useState('');
    const [loading, setLoading] = useState(true);

    // Partner products state
    const [partnerProducts, setPartnerProducts] = useState([]);
    const [partnerLoading, setPartnerLoading] = useState(false);
    const [partnerSyncStatus, setPartnerSyncStatus] = useState('idle');
    const [partnerSyncMessage, setPartnerSyncMessage] = useState('');

    // Partner filters state
    const [partnerActiveCategory, setPartnerActiveCategory] = useState('Todos');
    const [partnerActiveSubCategory, setPartnerActiveSubCategory] = useState('Todos');
    const [partnerMainCategories, setPartnerMainCategories] = useState(['Todos']);
    const [partnerSubCategories, setPartnerSubCategories] = useState({});
    const [partnerSearchTerm, setPartnerSearchTerm] = useState('');
    const [partnerDebouncedSearch, setPartnerDebouncedSearch] = useState('');
    const [partnerCurrentPage, setPartnerCurrentPage] = useState(1);
    const [partnerTotalPages, setPartnerTotalPages] = useState(1);
    const [partnerTotalProducts, setPartnerTotalProducts] = useState(0);

    // Debounce partner search
    useEffect(() => {
        const timer = setTimeout(() => {
            setPartnerDebouncedSearch(partnerSearchTerm);
            setPartnerCurrentPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [partnerSearchTerm]);

    // Load main products
    useEffect(() => {
        loadProducts();
    }, []);

    // Load partner products on component mount
    useEffect(() => {
        loadPartnerProducts();
        loadPartnerCategories();
        checkPartnerSyncStatus();
    }, []);

    // Load partner products when filters change
    useEffect(() => {
        loadPartnerProducts();
    }, [partnerCurrentPage, partnerActiveCategory, partnerActiveSubCategory, partnerDebouncedSearch]);

    // Load main products from API
    const loadProducts = async () => {
        try {
            const response = await fetch(`${ENV.API_BASE}/products`);
            if (response.ok) {
                const data = await response.json();
                setProducts(data);
            } else {
                console.error('Failed to load products:', response.status);
            }
        } catch (error) {
            console.error('Error loading products:', error);
        } finally {
            setLoading(false);
        }
    };

    // Load partner products
    const loadPartnerProducts = async () => {
        try {
            setPartnerLoading(true);
            const params = new URLSearchParams({
                page: partnerCurrentPage,
                limit: 8,
                ...(partnerActiveCategory !== 'Todos' && { category: partnerActiveCategory }),
                ...(partnerActiveSubCategory !== 'Todos' && { subCategory: partnerActiveSubCategory }),
                ...(partnerDebouncedSearch && { search: partnerDebouncedSearch })
            });

            const response = await fetch(`${ENV.API_BASE}/partner-products/products?${params}`);

            if (response.ok) {
                const data = await response.json();
                setPartnerProducts(data.products || []);
                setPartnerTotalPages(data.totalPages || 1);
                setPartnerTotalProducts(data.totalProducts || 0);

                if (data.syncStatus) {
                    setPartnerSyncStatus(data.syncStatus);
                } else if (data.products && data.products.length > 0) {
                    setPartnerSyncStatus('success');
                }
            } else {
                console.error('Failed to load products:', response.status);
                setPartnerProducts([]);
                setPartnerSyncStatus('error');
                setPartnerSyncMessage(`Error ${response.status}: ${response.statusText}`);
            }
        } catch (error) {
            console.error('Error loading products:', error);
            setPartnerProducts([]);
            setPartnerSyncStatus('error');
            setPartnerSyncMessage(error.message);
        } finally {
            setPartnerLoading(false);
        }
    };

    // Load partner categories
    const loadPartnerCategories = async () => {
        try {
            const response = await fetch(`${ENV.API_BASE}/partner-products/categories`);
            if (response.ok) {
                const data = await response.json();
                setPartnerMainCategories(data.mainCategories || ['Todos']);
                setPartnerSubCategories(data.subCategories || {});
            } else {
                console.error('Failed to load categories:', response.status);
            }
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    // Check partner sync status
    const checkPartnerSyncStatus = async () => {
        try {
            const response = await fetch(`${ENV.API_BASE}/partner-products/status`);
            if (response.ok) {
                const data = await response.json();
                setPartnerSyncStatus(data.syncStatus || 'idle');
                setPartnerSyncMessage(data.message || '');

                if (data.syncStatus === 'idle' || data.productCount === 0) {
                    triggerPartnerSync();
                }
            } else {
                console.error('Failed to check sync status:', response.status);
            }
        } catch (error) {
            console.error('Error checking sync status:', error);
        }
    };

    // Trigger partner sync
    const triggerPartnerSync = async () => {
        try {
            setPartnerSyncStatus('syncing');
            const response = await fetch(`${ENV.API_BASE}/partner-products/sync`, {
                method: 'POST'
            });

            if (response.ok) {
                setTimeout(() => {
                    checkPartnerSyncStatus();
                    loadPartnerProducts();
                }, 3000);
            } else {
                setPartnerSyncStatus('error');
                setPartnerSyncMessage('Failed to start sync');
            }
        } catch (error) {
            console.error('Error triggering sync:', error);
            setPartnerSyncStatus('error');
            setPartnerSyncMessage(error.message);
        }
    };

    // Get unique categories from main products
    const categories = useMemo(() => {
        return ['Todos', ...new Set(products.map(product => product.category))];
    }, [products]);

    // Filter main products by category
    const filteredProducts = useMemo(() => {
        return activeCategory === 'Todos'
            ? products
            : products.filter(product => product.category === activeCategory);
    }, [products, activeCategory]);

    // Open contact form
    const openContactForm = useCallback((productName = '') => {
        setProductToQuote(productName);
        setShowContactForm(true);
        if (isModalOpen) closeProductModal();
    }, [isModalOpen]);

    // Close contact form
    const closeContactForm = useCallback(() => {
        setShowContactForm(false);
        setProductToQuote('');
    }, []);

    // Open product modal - FIXED: track product type
    const openProductModal = useCallback((product, isPartner = false) => {
        setSelectedProduct(product);
        setSelectedProductType(isPartner ? 'partner' : 'main');
        setIsModalOpen(true);
    }, []);

    // Close product modal
    const closeProductModal = useCallback(() => {
        setSelectedProduct(null);
        setSelectedProductType('main');
        setIsModalOpen(false);
    }, []);

    // Partner filter handlers
    const handlePartnerCategoryChange = (category) => {
        setPartnerActiveCategory(category);
        setPartnerActiveSubCategory('Todos');
        setPartnerCurrentPage(1);
        const partnerSection = document.getElementById('partner-section');
        if (partnerSection) {
            partnerSection.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    const handlePartnerSubCategoryChange = (subCategory) => {
        setPartnerActiveSubCategory(subCategory);
        setPartnerCurrentPage(1);
        const partnerSection = document.getElementById('partner-section');
        if (partnerSection) {
            partnerSection.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    const handlePartnerSearchChange = (value) => {
        setPartnerSearchTerm(value);
    };

    const handlePartnerClearFilters = () => {
        setPartnerActiveCategory('Todos');
        setPartnerActiveSubCategory('Todos');
        setPartnerSearchTerm('');
        setPartnerDebouncedSearch('');
        setPartnerCurrentPage(1);
        const partnerSection = document.getElementById('partner-section');
        if (partnerSection) {
            partnerSection.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    const handlePartnerPageChange = (page) => {
        setPartnerCurrentPage(page);
        const partnerSection = document.getElementById('partner-section');
        if (partnerSection) {
            partnerSection.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    return (
        <section id="productos" className="py-12 bg-white relative">
            <div className="absolute inset-0 pointer-events-none">
                {[...Array(50)].map((_, s) => (
                    <motion.div
                        key={s}
                        className="absolute w-1 h-1 bg-secondary rounded-full"
                        style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%` }}
                        animate={{ opacity: [0.2, 1, 0.2], scale: [1, 1.5, 1] }}
                        transition={{ duration: Math.random() * 3 + 2, repeat: Infinity, delay: Math.random() * 2 }}
                    />
                ))}
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Main Products Section */}
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-8 md:mb-12"
                >
                    <h2 className="text-4xl md:text-7xl font-bold gradient-text mb-4 md:mb-6 drop-shadow-xl">
                        Productos
                    </h2>
                    <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                        ¡Productos personalizados de alta calidad!
                    </p>
                </motion.div>

                {/* Category filters for main products */}
                {categories.length > 1 && (
                    <div className="flex flex-wrap justify-center gap-2 mb-8">
                        {categories.map((category) => (
                            <button
                                key={category}
                                onClick={() => setActiveCategory(category)}
                                className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all ${activeCategory === category
                                    ? 'bg-primary text-white shadow-md shadow-primary/30'
                                    : 'bg-primary/10 text-gray-700 hover:bg-primary/20'
                                    }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                )}

                {/* Main products grid */}
                {loading ? (
                    <div className="text-center py-12">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
                        <p className="text-gray-500 mt-4">Cargando productos...</p>
                    </div>
                ) : filteredProducts.length > 0 ? (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                        {filteredProducts.map((product) => (
                            <ProductCard
                                key={product.id}
                                product={product}
                                onClick={(product) => openProductModal(product, false)}  // ← Main product, NOT partner
                            />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <p className="text-gray-500">No hay productos disponibles.</p>
                    </div>
                )}

                {/* Partner Products Section */}
                <motion.div
                    id="partner-section"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="mt-12 pt-8 border-t border-gray-200"
                >
                    <div className="text-center mb-6">
                        <h3 className="text-2xl md:text-3xl font-bold gradient-text mb-2">
                            Promocionales
                        </h3>
                        <div className="flex items-center justify-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${partnerSyncStatus === 'success' ? 'bg-green-500' :
                                partnerSyncStatus === 'syncing' ? 'bg-yellow-500 animate-pulse' :
                                    partnerSyncStatus === 'error' ? 'bg-red-500' : 'bg-gray-500'
                                }`} />
                        </div>
                    </div>

                    {/* Partner filters */}
                    <PartnerFilterBar
                        mainCategories={partnerMainCategories}
                        subCategories={partnerSubCategories}
                        activeCategory={partnerActiveCategory}
                        activeSubCategory={partnerActiveSubCategory}
                        onCategoryChange={handlePartnerCategoryChange}
                        onSubCategoryChange={handlePartnerSubCategoryChange}
                        totalProducts={partnerTotalProducts}
                        onClearFilters={handlePartnerClearFilters}
                        searchTerm={partnerSearchTerm}
                        onSearchChange={handlePartnerSearchChange}
                    />

                    {/* Partner products grid */}
                    {partnerLoading ? (
                        <div className="text-center py-12">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
                            <p className="text-gray-500 mt-4">Cargando catálogo...</p>
                        </div>
                    ) : partnerProducts.length > 0 ? (
                        <>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                {partnerProducts.map((product) => (
                                    <PartnerProductCard
                                        key={product.id}
                                        product={product}
                                        onClick={(product) => openProductModal(product, true)}  // ← Partner product
                                    />
                                ))}
                            </div>

                            {/* Pagination for partner products */}
                            {partnerTotalPages > 1 && (
                                <div className="flex justify-center items-center gap-2 mt-8">
                                    <button
                                        onClick={() => handlePartnerPageChange(partnerCurrentPage - 1)}
                                        disabled={partnerCurrentPage === 1}
                                        className="w-10 h-10 rounded-xl border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/10 hover:border-primary/30 transition-all flex items-center justify-center text-gray-600"
                                    >
                                        <ChevronLeft size={18} />
                                    </button>

                                    {/* Button for page -2 */}
                                    <button
                                        onClick={() => handlePartnerPageChange(partnerCurrentPage - 2)}
                                        disabled={partnerCurrentPage <= 2}
                                        className="w-10 h-10 rounded-xl border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/10 hover:border-primary/30 transition-all flex items-center justify-center text-gray-600"
                                    >
                                        <span className="px-4 py-2 text-gray-700">
                                            {partnerCurrentPage - 2 <= 0 ? "-" : partnerCurrentPage - 2}
                                        </span>
                                    </button>

                                    {/* Button for page -1 */}
                                    <button
                                        onClick={() => handlePartnerPageChange(partnerCurrentPage - 1)}
                                        disabled={partnerCurrentPage === 1}
                                        className="w-10 h-10 rounded-xl border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/10 hover:border-primary/30 transition-all flex items-center justify-center text-gray-600"
                                    >
                                        <span className="px-4 py-2 text-gray-700">
                                            {partnerCurrentPage - 1 <= 0 ? "-" : partnerCurrentPage - 1}
                                        </span>
                                    </button>

                                    {/* Current page indicator */}
                                    <span className="px-4 py-2 text-gray-700 font-semibold">
                                        Página {partnerCurrentPage} de {partnerTotalPages}
                                    </span>

                                    {/* Button for page +1 */}
                                    <button
                                        onClick={() => handlePartnerPageChange(partnerCurrentPage + 1)}
                                        disabled={partnerCurrentPage === partnerTotalPages}
                                        className="w-10 h-10 rounded-xl border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/10 hover:border-primary/30 transition-all flex items-center justify-center text-gray-600"
                                    >
                                        <span className="px-4 py-2 text-gray-700">
                                            {partnerCurrentPage + 1 > partnerTotalPages ? "-" : partnerCurrentPage + 1}
                                        </span>
                                    </button>

                                    {/* Button for page +2 */}
                                    <button
                                        onClick={() => handlePartnerPageChange(partnerCurrentPage + 2)}
                                        disabled={partnerCurrentPage + 1 >= partnerTotalPages}
                                        className="w-10 h-10 rounded-xl border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/10 hover:border-primary/30 transition-all flex items-center justify-center text-gray-600"
                                    >
                                        <span className="px-4 py-2 text-gray-700">
                                            {partnerCurrentPage + 2 > partnerTotalPages ? "-" : partnerCurrentPage + 2}
                                        </span>
                                    </button>

                                    <button
                                        onClick={() => handlePartnerPageChange(partnerCurrentPage + 1)}
                                        disabled={partnerCurrentPage === partnerTotalPages}
                                        className="w-10 h-10 rounded-xl border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/10 hover:border-primary/30 transition-all flex items-center justify-center text-gray-600"
                                    >
                                        <ChevronRight size={18} />
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-12">
                            <p className="text-gray-500">No hay productos disponibles en el catálogo.</p>
                            {partnerSyncStatus === 'error' && (
                                <button
                                    onClick={triggerPartnerSync}
                                    className="mt-4 px-6 py-2 bg-primary text-white rounded-xl hover:bg-primary/80 transition-colors"
                                >
                                    Reintentar sincronización
                                </button>
                            )}
                            {(partnerActiveCategory !== 'Todos' || partnerActiveSubCategory !== 'Todos' || partnerSearchTerm) && (
                                <button
                                    onClick={handlePartnerClearFilters}
                                    className="mt-4 px-6 py-2 bg-primary text-white rounded-xl hover:bg-primary/80 transition-colors ml-2"
                                >
                                    Limpiar filtros
                                </button>
                            )}
                        </div>
                    )}
                </motion.div>
            </div>

            {/* Product Detail Modal - FIXED: use selectedProductType */}
            <ProductDetailModal
                product={selectedProduct}
                isOpen={isModalOpen}
                onClose={closeProductModal}
                onQuote={openContactForm}
                isPartnerProduct={selectedProductType === 'partner'}  // ← Use tracked type
            />

            {/* Contact Form Modal */}
            <ContactForm
                key={productToQuote}
                isOpen={showContactForm}
                onClose={closeContactForm}
                initialProduct={productToQuote}
            />
        </section>
    );
};

export default Products;